/**
 * Self-hosted Sentry-style error reporter.
 * Ships every silent catch + uncaught error to POST /api/errors.
 * Surfaces in Admin → Errors dashboard.
 *
 * Phase B (cookie-only): the `tm_auth` httpOnly cookie is auto-attached via
 * `credentials: "include"` — no manual Authorization header needed.
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// In-memory de-dupe so a runaway loop doesn't spam the backend
const _recent = new Map(); // message -> timestamp
const DEDUPE_MS = 5000;

function _shouldSend(message) {
    const now = Date.now();
    const last = _recent.get(message);
    if (last && now - last < DEDUPE_MS) return false;
    _recent.set(message, now);
    // Trim map if it grows
    if (_recent.size > 200) {
        const cutoff = now - DEDUPE_MS * 4;
        for (const [k, t] of _recent) if (t < cutoff) _recent.delete(k);
    }
    return true;
}

/**
 * Report an error to the backend.
 * @param {unknown} error  Error instance, string, or any thrown value.
 * @param {object}  [meta] Optional { context, severity, page }.
 */
export function reportError(error, meta = {}) {
    try {
        const message =
            (error && error.message) ||
            (typeof error === "string" ? error : null) ||
            "Unknown error";
        if (!_shouldSend(message)) return;
        const payload = {
            source: "frontend",
            severity: meta.severity || "error",
            message: String(message).slice(0, 2000),
            stack: (error && error.stack) ? String(error.stack).slice(0, 8000) : null,
            page: meta.page || (typeof window !== "undefined" ? window.location.pathname : null),
            user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
            context: meta.context || {},
        };
        // Fire-and-forget; never throw from reporter
        fetch(`${API}/errors`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify(payload),
            keepalive: true,
        }).catch((err) => {
            // Reporter swallowing its own network failure — log to console only.
            console.warn("[errorReporter] POST /api/errors failed:", err?.message || err);
        });
    } catch (err) {
        console.warn("[errorReporter] internal failure:", err?.message || err);
    }
}

/**
 * Install global handlers. Call once from App root.
 */
export function installGlobalErrorHandlers() {
    if (typeof window === "undefined") return;
    if (window.__trErrorHandlersInstalled) return;
    window.__trErrorHandlersInstalled = true;

    window.addEventListener("error", (e) => {
        reportError(e.error || e.message || "window.error", {
            context: { type: "window.error", filename: e.filename, line: e.lineno, col: e.colno },
        });
    });

    window.addEventListener("unhandledrejection", (e) => {
        reportError(e.reason || "unhandledrejection", {
            context: { type: "unhandledrejection" },
        });
    });
}
